import { useEffect, useRef } from 'react';
import { Box, Paper, Typography, Divider } from '@mui/material';
import { ChatInput, type ChatInputHandle } from './ChatInput';
import { useChatContext } from '../../contexts/ChatContext';
import { VoiceControls } from '../voice/VoiceControls';
import { ELEVENLABS_PROXY_URL } from '../../services/workerUrls';
import { MessageBubble } from './MessageBubble';
import { useSoundEffects } from '../../hooks/useSoundEffects';
import type { ToolCallRecord } from '../../types';

interface ChatPanelProps {
  onToolCalls?: (toolCalls: ToolCallRecord[] | undefined) => void;
  hideInput?: boolean;
}

export function ChatPanel({ onToolCalls, hideInput }: ChatPanelProps) {
  const { messages, isLoading, sendMessage } = useChatContext();
  const lastMessageIdRef = useRef<string | null>(null);
  const chatInputRef = useRef<ChatInputHandle>(null);
  const responseAreaRef = useRef<HTMLDivElement>(null);
  const { playSound } = useSoundEffects();

  // Get last user message and assistant messages (phases + final narrative)
  const lastUserMessage = messages.filter(m => m.role === 'user').slice(-1)[0];
  const assistantMessages = messages.filter(m => m.role === 'assistant');
  const lastAssistantMessage = assistantMessages.slice(-1)[0];

  // Notify parent when new tool calls arrive
  useEffect(() => {
    if (messages.length === 0) return;

    const lastMessage = messages[messages.length - 1];
    if (
      lastMessage.role === 'assistant' &&
      lastMessage.id !== lastMessageIdRef.current &&
      lastMessage.toolCalls &&
      lastMessage.toolCalls.length > 0
    ) {
      lastMessageIdRef.current = lastMessage.id;
      onToolCalls?.(lastMessage.toolCalls);
    }
  }, [messages, onToolCalls]);

  // Handle transcribed speech - populate input field instead of auto-submitting
  const handleTranscript = (text: string) => {
    chatInputRef.current?.setValue(text);
  };

  // Auto-scroll to bottom of response area when last message updates
  useEffect(() => {
    if (!isLoading && responseAreaRef.current) {
      // Scroll to bottom to show the latest message
      responseAreaRef.current.scrollTop = responseAreaRef.current.scrollHeight;
    }
  }, [lastAssistantMessage?.id, isLoading]);

  return (
    <Box
      className="chat-panel"
      sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: { xs: 0, sm: 1 },
        border: 1,
        borderColor: 'divider',
        backgroundColor: 'background.paper',
        overflow: 'hidden',
      }}
    >
      {/* Last User Prompt */}
      {lastUserMessage && (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderBottom: 1,
            borderColor: 'divider',
            backgroundColor: 'action.hover',
            flexShrink: 0,
          }}
        >
          <Typography variant="caption" color="text.secondary" gutterBottom>
            Your Command:
          </Typography>
          <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
            {lastUserMessage.content}
          </Typography>
        </Paper>
      )}

      {/* Response Area (Progress or Final Narrative) - Fills remaining space */}
      <Box
        ref={responseAreaRef}
        sx={{
          flex: 1,
          overflow: 'auto',
          p: 2,
        }}
      >
        {lastAssistantMessage ? (
          <Box>
            {/* Display the latest assistant message (including phase progress) */}
            {lastAssistantMessage.content && (
              <MessageBubble message={lastAssistantMessage} playAudio={false} />
            )}
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: 'text.secondary',
              textAlign: 'center',
            }}
          >
            <Typography variant="h6" gutterBottom>
              Space Antics
            </Typography>
            <Typography variant="body2">
              Speak or type your command to begin...
            </Typography>
          </Box>
        )}
      </Box>

      {/* Voice Controls - Above input */}
      {!hideInput && lastAssistantMessage && !isLoading && (
        <>
          <Divider />
          <Box sx={{ px: 2, py: 1.5, flexShrink: 0 }}>
            <VoiceControls
              proxyUrl={ELEVENLABS_PROXY_URL()}
              narrative={lastAssistantMessage.content}
              onTranscript={handleTranscript}
              sfxPlayer={playSound}
              autoPlay={false}
            />
          </Box>
        </>
      )}

      {/* Input Area - At the bottom, full width */}
      {!hideInput && (
        <>
          <Divider />
          <Box
            className="chat-input-area"
            sx={{
              px: 2,
              py: 1.5,
              backgroundColor: 'background.paper',
              flexShrink: 0,
            }}
          >
            <ChatInput
              ref={chatInputRef}
              onSend={sendMessage}
              disabled={isLoading}
            />
          </Box>
        </>
      )}
    </Box>
  );
}
